import { Fragment, useEffect, useRef, useState } from 'react';
import { Unity, useUnityContext } from 'react-unity-webgl';
import { openInputField, closeInputField } from '../modal/ModalInput';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import NoSleep from 'nosleep.js';
import ExitIcon from '../assets/icons/CloseIcon.png';

const GamePage = () => {
    const wakeLockRef = useRef(null);
    const initializedRef = useRef(false);
    const unityContainerRef = useRef(null);
    const unityCanvasRef = useRef(null);
    const activeInputConfig = useRef(null);
    const activeInputRef = useRef(null);
    const activeInputIndexRef = useRef(0);
    const [refresh, setRefresh] = useState(false);
    const noSleepRef = useRef(new NoSleep());
    const [isDevice, setIsDevice] = useState(false);
    const VERSION = `${process.env.REACT_APP_VERSION || 'v0.0.0'}`;
    const [wakeLockActive, setWakeLockActive] = useState(false);
    const initialFocusLostRef = useRef(false);
    const forceUpdate = () => setRefresh((prev) => !prev);
    const turnOnToaster = useRef(false);

    const {
        unityProvider,
        loadingProgression,
        isLoaded,
        sendMessage,
        addEventListener,
        removeEventListener,
    } = useUnityContext({
        loaderUrl: 'unity_build/jeopardy-bar-league.loader.js',
        dataUrl: 'unity_build/jeopardy-bar-league.data.unityweb',
        frameworkUrl: 'unity_build/jeopardy-bar-league.framework.js.unityweb',
        codeUrl: 'unity_build/jeopardy-bar-league.wasm.unityweb',
        streamingAssetsUrl: 'StreamingAssets',
        print: function (message) {
            console.log(message); // Suppress alert and log messages instead
        },
        printErr: function (error) {
            console.error(error); // Log errors to the console instead of triggering an alert
        },
    });

    const onUnitySetLowQuality = (json) => {
        var value = JSON.parse(json);
        console.log(
            `[onUnityToggleFullscreen] unity requested to set LowQuality with value: ${value}`,
        );

        if (value === true) {
            localStorage.setItem('lowQuality', 'true');
        } else {
            localStorage.setItem('lowQuality', 'false');
        }

        window.location.reload();
    };

    const handleModalInputActive = () => {
        console.log('handleModalInputActive');
        if (unityCanvasRef.current) {
            unityCanvasRef.current.blur();
        }
    };

    const handleModalInputClosed = () => {
        console.log('handleModalInputClosed');
        if (unityCanvasRef.current) {
            unityCanvasRef.current.focus();
        }
    };

    const onUnityUpdateInputConfig = (json) => {
        console.log(`[onUnityUpdateInputConfig] got the value: ${json}`);
        const config = JSON.parse(json);
        if (config != null) {
            activeInputConfig.current = {};
            console.log(`[onUnityUpdateInputConfig] config is set: ${json}`);
        } else {
            activeInputConfig.current = null;
            console.log(`[onUnityUpdateInputConfig] config is null: ${json}`);
        }
    };

    useEffect(() => {
        window.addEventListener('onModalInputActive', handleModalInputActive);
        window.addEventListener('onModalInputClosed', handleModalInputClosed);

        return () => {
            window.removeEventListener('onModalInputActive', handleModalInputActive);
            window.removeEventListener('onModalInputClosed', handleModalInputClosed);
        };
    }, []);

    useEffect(() => {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (activeInputConfig.current) {
                    console.log(`activeInputConfig is set`);

                    // Handle added nodes (log when Unity adds input field)
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node;

                            if (
                                element.tagName === 'DIV' &&
                                element.querySelector("input[type='text']")
                            ) {
                                console.log('Unity input detected!');
                                const input = element.querySelector("input[type='text']");

                                // Disable autofill at the form level
                                const form = input.closest('form');
                                if (form) {
                                    form.setAttribute('autocomplete', 'off');
                                }

                                // Disable autofill, autocorrect, etc.
                                input.setAttribute('autocomplete', 'off');
                                input.setAttribute('autocorrect', 'off');
                                input.setAttribute('spellcheck', 'false');
                                input.setAttribute('autocapitalize', 'none');
                                input.setAttribute(
                                    'id',
                                    'no-autofill-' + Math.random().toString(36).substring(2),
                                ); // Randomize input attributes to prevent autofill
                                input.setAttribute(
                                    'name',
                                    'no-autofill-' + Math.random().toString(36).substring(2),
                                ); // Randomize input attributes to prevent autofill
                                input.setAttribute('type', 'search'); // Set input type to 'search' to suppress autofill suggestions

                                // Remove appearance (hides icons like key, card, map)
                                input.style.appearance = 'none';
                                input.style.webkitAppearance = 'none'; // Safari
                                input.style.mozAppearance = 'none'; // Firefox

                                input.addEventListener('input', (event) => {
                                    const cursorPosition = input.selectionStart; // Store the cursor position
                                    input.value = input.value.toUpperCase(); // Convert to uppercase
                                    input.setSelectionRange(cursorPosition, cursorPosition); // Restore cursor position
                                });

                                // Prevent text selection when reselecting input field
                                if (
                                    activeInputIndexRef.current &&
                                    activeInputIndexRef.current <= input.value.length &&
                                    activeInputIndexRef.current >= 0
                                ) {
                                    input.setSelectionRange(
                                        activeInputIndexRef.current,
                                        activeInputIndexRef.current,
                                    );
                                } else {
                                    input.setSelectionRange(input.value.length, input.value.length);
                                }

                                // Modify wrapper styles
                                element.style.position = 'absolute';
                                element.style.bottom = '120%';
                                element.style.border = 'none';
                                activeInputRef.current = input;

                                // Small delay to ensure rendering is updated (fix for iOS)
                                setTimeout(() => {
                                    // Modify input styles to ensure no screen shift
                                    if (activeInputRef.current) {
                                        activeInputRef.current.style.position = 'absolute';
                                        activeInputRef.current.style.bottom = '120%';
                                        activeInputRef.current.style.border = 'none';
                                        forceUpdate();
                                    }
                                }, 100);
                            }
                        }
                    });

                    // Handle removed nodes (log when Unity removes input field)
                    mutation.removedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE && activeInputConfig.current) {
                            const input = node.querySelector?.("input[type='text']");
                            if (input) {
                                console.log('Unity input field removed!');
                                activeInputRef.current = null;
                                activeInputIndexRef.current = 0;
                            }
                        }
                    });
                } else {
                    console.log(`activeInputConfig is null, ignoring input field override`);
                }
            });
        });

        observer.observe(document.body, { childList: true, subtree: true });
        return () => {
            activeInputRef.current = null;
            activeInputIndexRef.current = 0;
            observer.disconnect();
            console.log('Mutation observer disconnected');
        };
    }, []);

    const requestWakeLock = async () => {
        if (!('wakeLock' in navigator)) {
            console.log('[WakeLock] Not supported');
            return false;
        }

        if (wakeLockRef.current && !wakeLockRef.current.released) {
            return true;
        }

        try {
            if (document.visibilityState === 'visible' && !wakeLockRef.current) {
                const sentinel = await navigator.wakeLock.request('screen');
                sentinel.addEventListener('release', () => {
                    wakeLockRef.current = null;
                });
                wakeLockRef.current = sentinel;
                console.log('[WakeLock] Activated');
                return true;
            }
            return !!wakeLockRef.current;
        } catch (error) {
            console.error('[WakeLock] Error:', error);
            return false;
        }
    };

    const releaseWakeLock = async () => {
        if (wakeLockRef.current) {
            await wakeLockRef.current.release();
            wakeLockRef.current = null;
            setWakeLockActive(false);
        }
    };

    useEffect(() => {
        const detectAgent = async () => {
            const type = await checkDeviceType();
            // Set true if it's a mobile/tablet device
            setIsDevice(type.includes('Device'));
        };
        detectAgent();
    }, []);

    const checkDeviceType = async () => {
        if (navigator.userAgentData) {
            const hints = await navigator.userAgentData.getHighEntropyValues(['mobile']);
            return hints.mobile ? 'Device' : 'Desktop';
        }
        // Safari/Firefox regex
        return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'Device' : 'Desktop';
    };

    const handleInteraction = () => {
        if (wakeLockActive) {
            return;
        }

        // Request wakelock
        requestWakeLock()
            .then((success) => {
                if (success) {
                    console.log('[WakeLock] Success: Native wakelock activated.');
                    setWakeLockActive(true);
                } else {
                    console.warn(
                        '[WakeLock] Wakelock api failed or unsupported. Falling back to NoSleep...',
                    );

                    // Fallback to NoSleep
                    if (isDevice) {
                        try {
                            noSleepRef.current.enable();
                            setWakeLockActive(true);
                            console.log('[NoSleep] Success: fallback activated.');
                        } catch (nsError) {
                            console.error('[NoSleep] failed to activate:', nsError);
                        }
                    }
                }
            })
            .catch((err) => {
                // Handle error with wakelock request
                console.error('[WakeLock] Unable to activate wakelock:', err);

                // Final attempt at NoSleep
                if (isDevice) {
                    try {
                        noSleepRef.current.enable();
                        setWakeLockActive(true);
                        console.log('[NoSleep] Final fallback activated.');
                    } catch (fallbackErr) {
                        console.error('[NoSleep] Cannot activate NoSleep:', fallbackErr);
                    }
                }
            });
    };

    const onUnityOpenInputField = (json) => {
        const data = JSON.parse(json);
        console.log(
            `[onUnityOpenInputField] unity requested the input field with the data: ${JSON.stringify(data)}`,
        );
        openInputField(
            data.title,
            data.description,
            data.prefix,
            data.value,
            UnityOnInputFieldComplete,
        );
    };

    const onUnityCloseInputField = (json) => {
        console.log(`[onUnityCloseInputField] unity requested to close the input field: ${json}`);
        closeInputField();
    };

    const UnityOnInputFieldComplete = (value) => {
        console.log('[UnityOnInputFieldComplete] Input value:', value);
        sendMessage('UnityToWeb', 'OnInputFieldComplete', value);
    };

    const UnityGameConfig = (value) => {
        const gameConfigObj = JSON.stringify(value);
        sendMessage('UnityToWeb', 'OnReceivedGameConfig', gameConfigObj);
    };

    const onUnityGameConfig = () => {
        console.log(`[onUnityGameConfig] running version: ${VERSION}`);
        const gameConfig = {
            versionCode: VERSION,
        };
        UnityGameConfig(gameConfig);
    };

    const onUnityLeaveGame = (json) => {
        // json should be in for the format of = { "code": "theJoinCode", "nickname": "thePlayersName"}
        console.log(`[onUnityLeaveGame] unity request to leave the game with data: ${json}`);

        // Remove query parameters from the URL
        const baseUrl = window.location.origin + window.location.pathname;
        console.log('baseUrl:', baseUrl);
        window.history.replaceState({}, document.title, baseUrl);

        // Reload the page
        window.location.reload();
    };

    const onUnityCopyToClipboard = (text) => {
        console.log(
            `[UnityCopyToClipboard] unity request to CopyToClipboard with the text: ${text}`,
        );
        navigator.clipboard
            .writeText(text)
            .then(() => {
                console.log(`Copied text to clipboard: ${text}`);
                alert(`Copied text to clipboard: ${text}`);
            })
            .catch((error) => {
                console.error(`Could not copy text: ${error}`);
            });
    };

    const onUnityUpdateCursorPosition = (json) => {
        console.log(`[onUnityUpdateCursorPosition] got the value: ${json}`);
        if (activeInputRef.current) {
            activeInputIndexRef.current = JSON.parse(json);
            console.log(
                `[onUnityUpdateCursorPosition] We have a active input field: ${activeInputRef.current.id}, setting cursorPosition to: ${activeInputIndexRef.current}`,
            );
            activeInputRef.current.setSelectionRange(
                activeInputIndexRef.current,
                activeInputIndexRef.current,
            ); // Set the caret at the same position
        }
    };

    const onUnitySetupToaster = (json) => {
        console.log(`[onUnitySetupToaster] unity turned on toast notifications ${json}`);
        turnOnToaster.current = Boolean(json);
    };

    const onUnityToggleFullscreen = (json) => {
        const value = JSON.parse(json);
        console.log(
            `[onUnityToggleFullscreen] unity requested to toggle fullscreen with value: ${value}`,
        );

        const element = document.documentElement;

        if (value) {
            if (element.requestFullscreen) {
                element.requestFullscreen();
            } else if (element.mozRequestFullScreen) {
                element.mozRequestFullScreen();
            } else if (element.webkitRequestFullscreen) {
                element.webkitRequestFullscreen();
            } else if (element.msRequestFullscreen) {
                element.msRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
    };

    const handleFocusToast = () => {
        toast.error(
            <div className={'toast-text-container'}>
                <p className={'toast-title'}>Game was backgrounded!</p>
                <p className={'toast-message'}>Make sure to keep this window separate & active.</p>
            </div>,
            {
                toastId: 'game-backgrounded',
                position: 'top-right',
                autoClose: 15000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: 'colored',
                transition: Bounce,
                className: 'toast',
            },
        );
    };

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                requestWakeLock();
                if (initialFocusLostRef.current && turnOnToaster.current) {
                    handleFocusToast();
                }
            } else {
                releaseWakeLock();
                initialFocusLostRef.current = true;
                if (turnOnToaster.current) {
                    console.log(`Tab was backgrounded at: ${new Date().toLocaleTimeString()}`);
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            releaseWakeLock();
            // CLEANUP: Disable NoSleep when the component unmounts
            if (noSleepRef.current) {
                noSleepRef.current.disable();
                console.log('[NoSleep] Disabled on unmount');
            }
        };
    }, []);

    useEffect(() => {
        // Ensures toast element is kept visible in full screen mode, so host will get notifications when screen is backgrounded
        const toastEl = document.querySelector('.Toastify__toast-container');
        const originalParent = toastEl?.parentElement;

        const handleFullscreenChange = () => {
            if (!toastEl) {
                return;
            }
            if (document.fullscreenElement) {
                document.fullscreenElement.appendChild(toastEl);
            } else if (originalParent) {
                originalParent.appendChild(toastEl);
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            if (toastEl && originalParent) {
                originalParent.appendChild(toastEl);
            }
        };
    }, []);

    useEffect(() => {
        console.log(`[GamePage] isLoaded has changed: ${isLoaded}`);
        if (isLoaded) {
            console.log('[GamePage] Unity has completed loading');
            if (!initializedRef.current) {
                // Ensure that initialization occurs only once
                console.log('[GamePage.UnityAttachment] Attempting to attach rmbclient to unity');
                console.log(
                    `[GamePage.UnityAttachment] lowQuality Value: ${localStorage.getItem('lowQuality')}`,
                );

                // Attach Unity to Javscript Event Listeners
                addEventListener('UnityOnGameConfig', onUnityGameConfig);
                addEventListener('UnityLeaveGame', onUnityLeaveGame);
                addEventListener('UnityCopyToClipboard', onUnityCopyToClipboard);
                addEventListener('UnityOpenInputField', onUnityOpenInputField);
                addEventListener('UnityCloseInputField', onUnityCloseInputField);
                addEventListener('UnitySetLowQuality', onUnitySetLowQuality);
                addEventListener('UnityUpdateCursorPosition', onUnityUpdateCursorPosition);
                addEventListener('UpdateInputConfig', onUnityUpdateInputConfig);
                addEventListener('UnityToggleFullscreen', onUnityToggleFullscreen);
                addEventListener('UnitySetupToaster', onUnitySetupToaster);

                console.log('[GamePage.UnityAttachment] Finished attaching rmbclient to unity');

                // Finished setup
                initializedRef.current = true;
                console.log('[GamePage.UnityAttachment] completed unity setup');
                onUnityGameConfig();
            }
        }

        return () => {
            console.log(`[GamePage.UnityAttachment] exiting with ${initializedRef.current}`);
            if (initializedRef.current) {
                // Put all clean up under logic here (if needed)
                removeEventListener('UnityOnGameConfig', onUnityGameConfig);
                removeEventListener('UnityLeaveGame', onUnityLeaveGame);
                removeEventListener('UnityCopyToClipboard', onUnityCopyToClipboard);
                removeEventListener('UnityOpenInputField', onUnityOpenInputField);
                removeEventListener('UnityCloseInputField', onUnityCloseInputField);
                removeEventListener('UnitySetLowQuality', onUnitySetLowQuality);
                removeEventListener('UnityUpdateCursorPosition', onUnityUpdateCursorPosition);
                removeEventListener('UpdateInputConfig', onUnityUpdateInputConfig);
                removeEventListener('UnityToggleFullscreen', onUnityToggleFullscreen);
                removeEventListener('UnitySetupToaster', onUnitySetupToaster);

                console.log('[GamePage.UnityAttachment] Cleared all attachments to Unity');
                initializedRef.current = false;
                setWakeLockActive(false);
                turnOnToaster.current = false;
                console.log('[GamePage.UnityAttachment] Unity triggered pageClosed');
            }
        };
    }, [isLoaded]);

    const refreshPage = () => {
        window.location.reload();
        console.log('[GamePage] refreshing page...');
    };

    return (
        <Fragment>
            {!isLoaded && (
                <div>
                    <div className="exit-icon-container" onClick={() => refreshPage()}>
                        <img src={ExitIcon} alt="Exit" />
                    </div>
                    <div className="floating-element">
                        <div className="loading-progress">
                            <p>{Math.round(loadingProgression * 100)}%</p>
                        </div>
                        <p>Loading Game</p>
                    </div>
                </div>
            )}

            <div
                className="unity-container"
                ref={unityContainerRef}
                onClickCapture={handleInteraction}
                onTouchStartCapture={handleInteraction}
            >
                <Unity
                    unityProvider={unityProvider}
                    className="unity-content"
                    ref={unityCanvasRef}
                    style={{ visibility: isLoaded ? 'visible' : 'hidden' }}
                    devicePixelRatio={window.devicePixelRatio}
                />
                <ToastContainer
                    position="top-right"
                    autoClose={15000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick={false}
                    rtl={false}
                    pauseOnFocusLoss={false}
                    draggable
                    pauseOnHover
                    theme="light"
                    transition={Bounce}
                    toastClassName="toast-container"
                    bodyClassName="toast-body"
                    limit={1}
                />
            </div>
        </Fragment>
    );
};
export default GamePage;
